"use client";
import React, { createContext, useContext, useCallback, useState } from "react";
import { BASE_URL } from "../config/config";

const CategoryContext = createContext();

export const CategoryProvider = ({ children }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const token = localStorage.getItem("token");

    // 🔹 FETCH
    const fetchCategories = useCallback(async () => {
        if (!token) return;

        setLoading(true);
        try {
        const res = await fetch(`${BASE_URL}/categories`, {
            headers: {
            Authorization: `Bearer ${token}`,
            },
        });

        const data = await res.json();
        if (res.ok && data.status === 200) {
            setCategories(data.data || []);
        } else {
            setError(data.message || "Failed to fetch categories");
        }
        } catch {
        setError("Network error");
        } finally {
        setLoading(false);
        }
    }, [token]);

    // 🔹 ADD
    const addCategory = async (formData) => {
        try {
            // ✅ get audit id from localStorage
            const user = JSON.parse(localStorage.getItem("user") || "{}");
            const adminId = user.id; // fallback to 1 if not found

            // ✅ append to FormData
            formData.append("create_audit_id", adminId);

            const res = await fetch(`${BASE_URL}/categories`, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${token}`, // no content-type for FormData
            },
            body: formData,
            });

            const data = await res.json();

            if (res.ok && data.status === 201) {
            // ✅ add instantly to state
            fetchCategories(); // refresh list
            return true;
            } else {
            alert(data.message || "Add failed");
            }
        } catch (err) {
            alert("Network error");
        }
    };


    // 🔹 UPDATE
    const updateCategory = async (id, formData) => {
        try {
            // ✅ get audit id from localStorage
            const user = JSON.parse(localStorage.getItem("user") || "{}");
            const adminId = user.id; // fallback to 1 if not found

            // ✅ append update audit id
            formData.append("update_audit_id", adminId);

            const res = await fetch(`${BASE_URL}/categories/${id}`, {
            method: "POST", // keep as-is if backend expects POST
            headers: {
                Authorization: `Bearer ${token}`,
            },
            body: formData,
            });

            const data = await res.json();

            if (res.ok && data.status === 200) {
            fetchCategories(); // refresh list
            } else {
            alert(data.message || "Update failed");
            }
        } catch {
            alert("Network error");
        }
    };

    // 🔹 DELETE
    const deleteCategory = async (id) => {
        if (!window.confirm("Delete this category?")) return;

        try {
            const user = JSON.parse(localStorage.getItem("user") || "{}");
            const adminId = user.id;

            const res = await fetch(`${BASE_URL}/categories/${id}`, {
            method: "DELETE",
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json", // ✅ REQUIRED
            },
            body: JSON.stringify({
                delete_audit_id: adminId,
            }),
            });

            const data = await res.json();

            if (res.ok && data.status === 200) {
            setCategories((prev) => prev.filter((c) => c.id !== id));
            } else {
            alert(data.message || "Delete failed");
            }
        } catch {
            alert("Delete failed");
        }
    };


    // 🔹 STATUS TOGGLE
    const toggleCategoryStatus = async (id, currentStatus) => {
        const newStatus = currentStatus === "Active" ? "Inactive" : "Active";

        try {
            const user = JSON.parse(localStorage.getItem("user") || "{}");
            const adminId = user.id;

            const res = await fetch(`${BASE_URL}/categories/${id}/status`, {
            method: "PATCH",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
                status: newStatus,
                update_audit_id: adminId, // ✅ added
            }),
            });

            const data = await res.json();

            if (res.ok && data.status === 200) {
            setCategories((prev) =>
                prev.map((c) =>
                c.id === id ? { ...c, status: newStatus } : c
                )
            );
            } else {
            alert(data.message || "Status update failed");
            }
        } catch {
            alert("Status update failed");
        }
    };

  return (
    <CategoryContext.Provider
      value={{
        categories,
        loading,
        error,
        fetchCategories,
        addCategory,
        updateCategory,
        deleteCategory,
        toggleCategoryStatus,
      }}
    >
      {children}
    </CategoryContext.Provider>
  );
};

export const useCategories = () => useContext(CategoryContext);
